import type { BarcosRoute } from '$lib/geo';
import * as m from '$lib/paraglide/messages.js';
import type { FaqSchemaItem, MarketingSeoMeta } from '$lib/utils/marketing-seo';

export type MarketplaceSeoKey = 'spain' | 'galicia';

export interface GuideLink {
	label: string;
	href: string;
}

export interface GuideSection {
	id: string;
	title: string;
	paragraphs: string[];
	links?: GuideLink[];
}

export interface MarketplaceSeoContent {
	meta: MarketingSeoMeta;
	reviewsTitle: string;
	reviewsSubtitle: string;
	guideSections: GuideSection[];
	faqTitle: string;
	faqs: MarketplaceFaqItem[];
}

export interface MarketplaceFaqItem extends FaqSchemaItem {
	answerVariant?: 'license-table';
}

export function getMarketplaceSeoKey(route: BarcosRoute): MarketplaceSeoKey | null {
	if (route.listingId || route.provincia || route.ciudadSlug || route.puertoSlug) {
		return null;
	}
	if (route.comunidad?.slug === 'galicia') {
		return 'galicia';
	}
	if (!route.comunidad) {
		return 'spain';
	}
	return null;
}

export function getMarketplaceSeoContent(key: MarketplaceSeoKey): MarketplaceSeoContent {
	if (key === 'galicia') {
		return {
			meta: {
				title: m.marketplace_seo_galicia_meta_title(),
				description: m.marketplace_seo_galicia_meta_description()
			},
			reviewsTitle: m.marketplace_seo_galicia_reviews_title(),
			reviewsSubtitle: m.marketplace_seo_galicia_reviews_subtitle(),
			guideSections: [
				{
					id: 'intro',
					title: m.marketplace_seo_galicia_guide_intro_title(),
					paragraphs: [
						m.marketplace_seo_galicia_guide_intro_p1(),
						m.marketplace_seo_galicia_guide_intro_p2()
					],
					links: [
						{ label: 'Pontevedra', href: '/barcos/galicia/pontevedra' },
						{ label: 'A Coruña', href: '/barcos/galicia/a-coruna' },
						{ label: 'Experiencias en Galicia', href: '/barcos/experiencia/galicia' }
					]
				},
				{
					id: 'salvora',
					title: m.marketplace_seo_galicia_guide_salvora_title(),
					paragraphs: [
						m.marketplace_seo_galicia_guide_salvora_p1(),
						m.marketplace_seo_galicia_guide_salvora_p2()
					],
					links: [{ label: 'Experiencias en Galicia', href: '/barcos/experiencia/galicia' }]
				},
				{
					id: 'cies',
					title: m.marketplace_seo_galicia_guide_cies_title(),
					paragraphs: [
						m.marketplace_seo_galicia_guide_cies_p1(),
						m.marketplace_seo_galicia_guide_cies_p2()
					]
				},
				{
					id: 'mussels',
					title: m.marketplace_seo_galicia_guide_mussels_title(),
					paragraphs: [
						m.marketplace_seo_galicia_guide_mussels_p1(),
						m.marketplace_seo_galicia_guide_mussels_p2()
					],
					links: [{ label: 'Experiencias en Galicia', href: '/barcos/experiencia/galicia' }]
				},
				{
					id: 'rias',
					title: m.marketplace_seo_galicia_guide_rias_title(),
					paragraphs: [
						m.marketplace_seo_galicia_guide_rias_p1(),
						m.marketplace_seo_galicia_guide_rias_p2()
					],
					links: [{ label: 'Alquiler en Galicia', href: '/barcos/alquiler/galicia' }]
				}
			],
			faqTitle: m.marketplace_seo_galicia_faq_title(),
			faqs: [
				{
					question: m.marketplace_seo_galicia_faq_q1(),
					answer: m.marketplace_seo_galicia_faq_a1()
				},
				{
					question: m.marketplace_seo_galicia_faq_q2(),
					answer: m.marketplace_seo_galicia_faq_a2()
				},
				{
					question: m.marketplace_seo_galicia_faq_q3(),
					answer: m.marketplace_seo_galicia_faq_a3()
				},
				{
					question: m.marketplace_seo_galicia_faq_q4(),
					answer: m.marketplace_seo_galicia_faq_a4()
				},
				{
					question: m.marketplace_seo_galicia_faq_q5(),
					answer: m.marketplace_seo_galicia_faq_a5()
				},
				{ question: m.marketplace_seo_galicia_faq_q6(), answer: m.marketplace_seo_galicia_faq_a6() }
			]
		};
	}

	return {
		meta: {
			title: m.marketplace_seo_spain_meta_title(),
			description: m.marketplace_seo_spain_meta_description()
		},
		reviewsTitle: m.marketplace_seo_spain_reviews_title(),
		reviewsSubtitle: m.marketplace_seo_spain_reviews_subtitle(),
		guideSections: [
			{
				id: 'intro',
				title: m.marketplace_seo_spain_guide_intro_title(),
				paragraphs: [
					m.marketplace_seo_spain_guide_intro_p1(),
					m.marketplace_seo_spain_guide_intro_p2()
				],
				links: [
					{ label: 'Galicia', href: '/barcos/galicia' },
					{ label: 'Islas Baleares', href: '/barcos/islas-baleares' },
					{ label: 'Experiencias náuticas', href: '/barcos/experiencia' }
				]
			},
			{
				id: 'no-license',
				title: m.marketplace_seo_spain_guide_no_license_title(),
				paragraphs: [
					m.marketplace_seo_spain_guide_no_license_p1(),
					m.marketplace_seo_spain_guide_no_license_p2()
				],
				links: [{ label: 'Alquiler de barcos', href: '/barcos/alquiler' }]
			},
			{
				id: 'with-skipper',
				title: m.marketplace_seo_spain_guide_with_skipper_title(),
				paragraphs: [
					m.marketplace_seo_spain_guide_with_skipper_p1(),
					m.marketplace_seo_spain_guide_with_skipper_p2()
				]
			},
			{
				id: 'experiences',
				title: m.marketplace_seo_spain_guide_experiences_title(),
				paragraphs: [
					m.marketplace_seo_spain_guide_experiences_p1(),
					m.marketplace_seo_spain_guide_experiences_p2()
				],
				links: [{ label: 'Excursiones y experiencias', href: '/barcos/experiencia' }]
			}
		],
		faqTitle: m.marketplace_seo_spain_faq_title(),
		faqs: [
			{ question: m.marketplace_seo_spain_faq_q1(), answer: m.marketplace_seo_spain_faq_a1() },
			{ question: m.marketplace_seo_spain_faq_q2(), answer: m.marketplace_seo_spain_faq_a2() },
			{
				question: m.marketplace_seo_spain_faq_q7(),
				answer: m.marketplace_seo_spain_faq_a7(),
				answerVariant: 'license-table'
			},
			{ question: m.marketplace_seo_spain_faq_q3(), answer: m.marketplace_seo_spain_faq_a3() },
			{ question: m.marketplace_seo_spain_faq_q4(), answer: m.marketplace_seo_spain_faq_a4() },
			{ question: m.marketplace_seo_spain_faq_q5(), answer: m.marketplace_seo_spain_faq_a5() },
			{ question: m.marketplace_seo_spain_faq_q6(), answer: m.marketplace_seo_spain_faq_a6() }
		]
	};
}
